// iter-121.116 Enhancement A — Real per-action drawer bodies.
//
// Each entry in `RENDERERS` is a thin component that fetches its row-
// level data from the matching `/api/home/cockpit/actions/{key}` detail
// endpoint and renders a table with inline action buttons. Inline writes
// hit existing platform endpoints (extend-trial, resend-otp, suspend
// tenant) so the SA stays on Home for the common cases.
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ExternalLink, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";
import { useCockpitScope } from "@/lib/cockpit-scope";

function timeFromNow(iso) {
    if (!iso) return "—";
    const diff = (new Date(iso).getTime() - Date.now()) / 1000;
    const sign = diff < 0 ? "-" : "";
    const abs = Math.abs(diff);
    if (abs < 3600) return `${sign}${Math.round(abs / 60)}m`;
    if (abs < 86400) return `${sign}${Math.round(abs / 3600)}h`;
    return `${sign}${Math.round(abs / 86400)}d`;
}

function fmtMs(n) {
    if (!n) return "—";
    return n >= 1000 ? `${(n / 1000).toFixed(2)}s` : `${Math.round(n)}ms`;
}

function DrawerTable({ columns, rows, emptyMsg, testidPrefix }) {
    if (!rows || rows.length === 0) {
        return (
            <div className="text-xs text-muted-foreground py-8 text-center"
                 data-testid={`${testidPrefix}-empty`}>
                {emptyMsg || "No rows."}
            </div>
        );
    }
    return (
        <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full text-[12px]" data-testid={`${testidPrefix}-table`}>
                <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    <tr>
                        {columns.map((c) => (
                            <th key={c.key} className={`text-left px-2 py-1.5 ${c.align === "right" ? "text-right" : ""}`}>
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.id || r.tenant_id || r.user_id || r.connector_id || r.query_id || i}
                            className="border-t border-border"
                            data-testid={`${testidPrefix}-row-${i}`}>
                            {columns.map((c) => (
                                <td key={c.key}
                                    className={`px-2 py-1.5 ${c.align === "right" ? "text-right" : ""}`}>
                                    {c.render ? c.render(r) : (r[c.key] ?? "—")}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function useDrawerData(key, queryString) {
    const [rows, setRows] = useState(null);
    const [reloadAt, setReloadAt] = useState(0);
    useEffect(() => {
        setRows(null);
        api.get(`/home/cockpit/actions/${key}?${queryString || ""}`)
            .then((r) => setRows(r.data?.rows || []))
            .catch(() => setRows([]));
    }, [key, queryString, reloadAt]);
    const reload = useCallback(() => setReloadAt(Date.now()), []);
    return [rows, reload];
}

function DrawerHeader({ onReload, onNavigateAll, label }) {
    return (
        <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                {label || "Detail rows"}
            </div>
            <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={onReload}
                        className="h-7 px-2" data-testid="drawer-reload">
                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reload
                </Button>
                {onNavigateAll && (
                    <Button size="sm" variant="outline" onClick={onNavigateAll}
                            className="h-7 px-2" data-testid="drawer-open-all">
                        Open full view <ExternalLink className="h-3.5 w-3.5 ml-1" />
                    </Button>
                )}
            </div>
        </div>
    );
}

// ── Trials expiring ─────────────────────────────────────────────────────
function TrialsExpiringDrawer({ onNavigate }) {
    const { queryString } = useCockpitScope();
    const [rows, reload] = useDrawerData("trials-expiring", queryString);
    const extend = async (tenantId, name) => {
        try {
            const r = await api.post(`/home/cockpit/actions/extend-trial/${tenantId}`, { days: 7 });
            toast.success(`Trial extended for ${name} → ${new Date(r.data?.trial_ends_at).toLocaleDateString()}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to extend trial");
        }
    };
    const columns = [
        { key: "org_name",      label: "Org", render: (r) => <span className="font-medium">{r.org_name}</span> },
        { key: "tenant_name",   label: "Tenant" },
        { key: "owner_email",   label: "Owner",
          render: (r) => <span className="text-muted-foreground text-[11px]">{r.owner_email}</span> },
        { key: "trial_ends_at", label: "Ends",   align: "right",
          render: (r) => <span className="text-amber-600 dark:text-amber-400">{timeFromNow(r.trial_ends_at)}</span> },
        { key: "users_count",   label: "Users",  align: "right" },
        { key: "_actions",      label: "",       align: "right",
          render: (r) => (
              <div className="flex items-center gap-1 justify-end">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                          onClick={() => extend(r.tenant_id, r.tenant_name)}
                          data-testid={`trial-extend-${r.tenant_id}`}>
                      +7d
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                          onClick={() => onNavigate(`/operations?tenant=${r.tenant_id}`)}
                          data-testid={`trial-open-${r.tenant_id}`}>
                      Open
                  </Button>
              </div>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/operations?lifecycle=trial")}
                label="Trial tenants ending in ≤ 7 days" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading trials…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No trials expiring in the next 7 days."
                               testidPrefix="trials-expiring" />}
        </>
    );
}

// ── Orgs over AI budget ────────────────────────────────────────────────
function OrgsOverBudgetDrawer({ onNavigate }) {
    const { queryString } = useCockpitScope();
    const [rows, reload] = useDrawerData("orgs-over-budget", queryString);
    const columns = [
        { key: "org_name",      label: "Org", render: (r) => <span className="font-medium">{r.org_name}</span> },
        { key: "tenant_name",   label: "Tenant" },
        { key: "used_pct",      label: "Used", align: "right",
          render: (r) => {
              const tone = r.used_pct >= 100 ? "text-destructive"
                         : r.used_pct >= 90 ? "text-amber-600 dark:text-amber-400"
                         : "text-foreground";
              return <span className={`font-mono tabular-nums ${tone}`}>{r.used_pct}%</span>;
          },
        },
        { key: "used",          label: "Used / Cap", align: "right",
          render: (r) => (
              <span className="text-[11px] text-muted-foreground">
                  {r.used.toLocaleString()} / {r.cap.toLocaleString()} cr
              </span>
          ),
        },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => onNavigate(`/settings/ai-budgets?tenant=${r.tenant_id}`)}
                      data-testid={`budget-open-${r.tenant_id}`}>
                  Adjust
              </Button>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/settings/ai-budgets")}
                label="Orgs at ≥ 80% of monthly AI credit cap" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading budgets…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No org is within 20% of its cap."
                               testidPrefix="orgs-over-budget" />}
        </>
    );
}

// ── Failing connectors ─────────────────────────────────────────────────
function ConnectorsFailingDrawer({ onNavigate }) {
    const { queryString } = useCockpitScope();
    const [rows, reload] = useDrawerData("connectors-failing", queryString);
    const columns = [
        { key: "org_name",       label: "Org", render: (r) => <span className="font-medium">{r.org_name}</span> },
        { key: "connector_name", label: "Connector" },
        { key: "source_type",    label: "Type",
          render: (r) => <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{r.source_type}</span> },
        { key: "last_sync_at",   label: "Failed", align: "right",
          render: (r) => <span className="text-destructive">{timeFromNow(r.last_sync_at)}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => onNavigate(`/data?connector=${r.connector_id}`)}
                      data-testid={`conn-diagnose-${r.connector_id}`}>
                  Diagnose
              </Button>
          ),
        },
    ];
    const drilldown = rows && rows[0] ? (
        <div className="mt-2 text-[11px] text-muted-foreground italic">
            Latest error: <span className="text-foreground">{rows[0].last_error}</span>
        </div>
    ) : null;
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/operations?filter=connector-err")}
                label="Connectors that failed in the last 24h" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading connectors…</div>
                : (
                    <>
                        <DrawerTable columns={columns} rows={rows}
                                     emptyMsg="No connector failures in the last 24h."
                                     testidPrefix="connectors-failing" />
                        {drilldown}
                    </>
                )}
        </>
    );
}

// ── Slow queries ───────────────────────────────────────────────────────
function SlowQueriesDrawer({ onNavigate }) {
    const { queryString } = useCockpitScope();
    const [rows, reload] = useDrawerData("slow-queries", queryString);
    const columns = [
        { key: "org_name",     label: "Org", render: (r) => <span className="font-medium">{r.org_name}</span> },
        { key: "user_email",   label: "User",
          render: (r) => <span className="text-muted-foreground text-[11px]">{r.user_email}</span> },
        { key: "provider",     label: "Provider",
          render: (r) => <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{r.provider}</span> },
        { key: "elapsed_ms",   label: "Elapsed", align: "right",
          render: (r) => <span className="text-amber-600 dark:text-amber-400 font-mono tabular-nums">{fmtMs(r.elapsed_ms)}</span> },
        { key: "created_at",   label: "When", align: "right",
          render: (r) => <span className="text-[11px] text-muted-foreground">{timeFromNow(r.created_at)}</span> },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/audit-activity?filter=slow-queries")}
                label="Queries > 30s in the last 24h" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading queries…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No queries exceeded 30s today."
                               testidPrefix="slow-queries" />}
        </>
    );
}

// ── Email templates pending ────────────────────────────────────────────
function EmailTemplatesDrawer({ onNavigate }) {
    const [rows, reload] = useDrawerData("email-templates-pending", "");
    const columns = [
        { key: "name", label: "Template",
          render: (r) => <span className="font-medium">{r.name}</span> },
        { key: "kind", label: "Kind",
          render: (r) => <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{r.kind || "—"}</span> },
        { key: "updated_at", label: "Updated", align: "right",
          render: (r) => timeFromNow(r.updated_at || r.created_at) },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => onNavigate(`/settings/branding/email?template=${r.id}`)}
                      data-testid={`email-open-${r.id}`}>
                  Review
              </Button>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/settings/branding/email")}
                label="Email templates awaiting publish" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading templates…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No draft email templates."
                               testidPrefix="email-templates-pending" />}
        </>
    );
}

// ── Stalled signups ────────────────────────────────────────────────────
function StalledSignupsDrawer({ onNavigate }) {
    const { queryString } = useCockpitScope();
    const [rows, reload] = useDrawerData("stalled-signups", queryString);
    const resendOtp = async (userId, email) => {
        try {
            const r = await api.post(`/home/cockpit/actions/resend-otp/${userId}`);
            if (r.data?.ok) toast.success(`OTP resent to ${email}`);
            else toast.error(r.data?.message || "Failed to resend OTP");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to resend OTP");
        }
    };
    const columns = [
        { key: "org_name", label: "Org", render: (r) => <span className="font-medium">{r.org_name}</span> },
        { key: "email",    label: "Email",
          render: (r) => <span className="text-[11px]">{r.email}</span> },
        { key: "created_at", label: "Joined", align: "right",
          render: (r) => <span className="text-muted-foreground">{timeFromNow(r.created_at)}</span> },
        { key: "_actions", label: "", align: "right",
          render: (r) => (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]"
                      onClick={() => resendOtp(r.user_id, r.email)}
                      data-testid={`signup-resend-${r.user_id}`}>
                  <Mail className="h-3 w-3 mr-1" /> Resend OTP
              </Button>
          ),
        },
    ];
    return (
        <>
            <DrawerHeader onReload={reload}
                onNavigateAll={() => onNavigate("/audit-activity?filter=signup-pending")}
                label="Users unverified for > 24h" />
            {rows === null
                ? <div className="text-xs text-muted-foreground py-4">Loading users…</div>
                : <DrawerTable columns={columns} rows={rows}
                               emptyMsg="No stalled signups."
                               testidPrefix="stalled-signups" />}
        </>
    );
}

const RENDERERS = {
    trials_expiring_7d:      TrialsExpiringDrawer,
    orgs_over_budget:        OrgsOverBudgetDrawer,
    connectors_failing_24h:  ConnectorsFailingDrawer,
    slow_queries_today:      SlowQueriesDrawer,
    email_templates_pending: EmailTemplatesDrawer,
    signups_stalled_24h:     StalledSignupsDrawer,
};

export default function ActionDrawer({ item, onClose, onNavigate }) {
    const Renderer = RENDERERS[item.key];
    if (!Renderer) {
        return (
            <div className="text-xs text-muted-foreground py-8 text-center"
                 data-testid="action-drawer-unknown">
                No detail view available for `{item.key}`.
            </div>
        );
    }
    return <Renderer onClose={onClose} onNavigate={onNavigate} />;
}
